import {
  ConfirmAssignDialog,
  ConflictAssignDialog,
  DeleteSiteDialog,
  MapDeleteConfirmDialog,
  RemoveAssignmentDialog,
} from './AssignmentDialogs';
import { AddressSearch } from './AddressSearch';
import { NewSiteDialog } from './NewSiteDialog';
import { SitePanel } from './SitePanel';
import { ProjectShape, useShapeService } from '@/api/services/shapeService';
import { useOrg } from '@/organization/useOrg';
import { fetchSubprojects } from '@/services/subprojectService';
import { Subproject } from '@/stores/SubprojectStore';
import { Routes } from '@/CSIRouter';
import CheckIcon from '@mui/icons-material/Check';
import ReplyIcon from '@mui/icons-material/Reply';
import { BaseDecarbFooterBar } from '@/decarbonization/components/BaseDecarbFooterBar';
import {
  Box,
  Button,
  FormControl,
  IconButton,
  MenuItem,
  Select,
  Snackbar,
  Typography,
} from '@mui/material';
import L from 'leaflet';
import 'leaflet-draw';
import 'leaflet-draw/dist/leaflet.draw.css';
import 'leaflet/dist/leaflet.css';
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useProjectStore } from '@/stores/ProjectStore';
import { apiFetch } from '@/util/apiClient';
import { subprojectEndpoints } from '@/api/endpoints/subprojects';
import { usePencilsDown } from '@/decarbonization/hooks/usePencilsDown.ts';

// Customizes vertex points
import './SiteSelectionPage.css';

// Fix Leaflet default marker icons (vite asset issue)
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
});

const NEUTRAL_COLOR = '#00008B'; // dark navy — unassigned shapes
//const SELECTED_COLOR = '#FF9800';     // amber — selected/active shape
const SELECTED_COLOR = '#FFF600'; // yellow — selected/active shape
const ZIP_BOUNDARY_COLOR = '#3388ff'; // blue — zip code boundary lines

// Default colors assigned by shape index within the project.
// All dark navy for now — swap in distinct colors when custom color UX is ready.
const SITE_COLOR_DEFAULTS = [
  '#00008B',
  '#d40808',
  '#1a990e',
  '#680e99',
  '#d67e0b',
  '#ba079c',
];

async function reverseGeocode(
  lat: number,
  lon: number,
): Promise<string | null> {
  try {
    // NOTE: Browsers silently drop the User-Agent header (Fetch spec forbidden header).
    // Nominatim will not see this value. Proxying via Django would be needed to properly
    // identify requests. The per-draw throttle partially mitigates ToS concerns.
    const resp = await fetch(
      `https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lon}&format=json`,
      { headers: { 'User-Agent': 'CSI-App/1.0 (climatesolutionsintl.org)' } },
    );
    const data = await resp.json();
    return (data.display_name as string) || null;
  } catch {
    return null;
  }
}

interface AssignPending {
  type: 'confirm' | 'conflict';
  shapeId: string;
  shapeName: string;
  currentSiteName?: string;
}

interface MapDeletePending {
  layers: any[];
  shapeIds: string[];
  names: string[];
}

interface DeletePending {
  shapeId: string;
  shapeName: string;
  subprojectNames: string[];
}

interface RemovePending {
  subprojectId: string;
  subprojectName: string;
  shapeId: string;
}

export function SiteSelectionPage() {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const handleBack = useCallback(() => navigate(-1), [navigate]);
  const handleSummary = useCallback(
    () => navigate(`/project/${projectId}/proj-details`),
    [navigate, projectId],
  );
  const [searchParams] = useSearchParams();
  const shapeService = useShapeService();
  const shapeServiceRef = useRef(shapeService);
  const projectStore = useProjectStore();
  const project = projectId ? projectStore.get(projectId) : undefined;
  const { selectedOrg } = useOrg();
  const pencilsDown = usePencilsDown();

  const mapRef = useRef<L.Map | null>(null);
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const drawnItemsRef = useRef<L.FeatureGroup>(new L.FeatureGroup());
  const zipBoundsRef = useRef<L.FeatureGroup>(new L.FeatureGroup());
  const pendingLayerRef = useRef<any>(null);
  const savedShapesCountRef = useRef(0);
  const savedShapesRef = useRef<ProjectShape[]>([]);

  const [savedShapes, setSavedShapes] = useState<ProjectShape[]>([]);
  const [selectedShapeId, setSelectedShapeId] = useState<string | null>(null);
  const selectedShapeIdRef = useRef<string | null>(null);
  const [showZipBounds, setShowZipBounds] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogDefaultName, setDialogDefaultName] = useState('Site A');

  // Subprojects
  const [subprojects, setSubprojects] = useState<Subproject[]>([]);
  const [activeSubprojectId, setActiveSubprojectId] = useState<string | null>(
    null,
  );
  const activeSubprojectIdRef = useRef<string | null>(null);
  const subprojectsRef = useRef<Subproject[]>([]);

  // Snackbar
  const [snackbar, setSnackbar] = useState<string | null>(null);

  // Refresh state — callback defined after loadShapes below
  const [refreshing, setRefreshing] = useState(false);

  // Map tool mode tracking
  const editModeActiveRef = useRef(false);
  const deleteModeActiveRef = useRef(false);

  // Assignment dialogs
  const [assignPending, setAssignPending] = useState<AssignPending | null>(
    null,
  );
  const [deletePending, setDeletePending] = useState<DeletePending | null>(
    null,
  );
  const [removePending, setRemovePending] = useState<RemovePending | null>(
    null,
  );
  const [mapDeletePending, setMapDeletePending] =
    useState<MapDeletePending | null>(null);

  // Keep refs current
  useEffect(() => {
    shapeServiceRef.current = shapeService;
  }, [shapeService]);

  useEffect(() => {
    savedShapesCountRef.current = savedShapes.length;
    savedShapesRef.current = savedShapes;
  }, [savedShapes]);

  useEffect(() => {
    activeSubprojectIdRef.current = activeSubprojectId;
  }, [activeSubprojectId]);

  useEffect(() => {
    subprojectsRef.current = subprojects;
  }, [subprojects]);

  // Aggregate zipcodes across all saved shapes for zip boundary fetching
  const zipcodes = useMemo(() => {
    const all = new Set<string>();
    for (const s of savedShapes) {
      for (const z of s.properties.intersectingZipcodes || []) all.add(z);
    }
    return Array.from(all).sort();
  }, [savedShapes]);

  // INCOMPLETE DATA: true when any subproject has no site assigned
  const isIncomplete = subprojects.some((s) => !s.projectShape);

  const highlightLayer = useCallback((layer: any) => {
    if (layer.setStyle) {
      layer.setStyle({
        color: SELECTED_COLOR,
        fillColor: SELECTED_COLOR,
        weight: 4,
      });
    }
  }, []);

  const unhighlightLayer = useCallback((layer: any) => {
    const color = layer._shapeColor || NEUTRAL_COLOR;
    if (layer.setStyle) {
      layer.setStyle({ color, fillColor: color, weight: 3 });
    }
  }, []);

  const selectShape = useCallback(
    (shapeId: string | null) => {
      drawnItemsRef.current.eachLayer((layer: any) => unhighlightLayer(layer));
      if (shapeId) {
        drawnItemsRef.current.eachLayer((layer: any) => {
          if (layer._shapeId === shapeId) highlightLayer(layer);
        });
      }
      setSelectedShapeId(shapeId);
      selectedShapeIdRef.current = shapeId;
    },
    [highlightLayer, unhighlightLayer],
  );

  // DEV: log geo/cost factors, electric rates + utility providers to console when a
  // shape is selected. Gated to the dev build (Vite statically strips `import.meta.env.DEV`
  // blocks from production bundles), so neither the logs nor the diagnostic fetch ship to prod.
  useEffect(() => {
    if (!import.meta.env.DEV) return;
    if (!selectedShapeId || !projectId || !selectedOrg) return;
    const shape = savedShapes.find((s) => s.id === selectedShapeId);
    if (!shape) return;
    console.log('[cost-factors]', {
      intersectingZipcodes: shape.properties.intersectingZipcodes,
      nearestCity: shape.properties.nearestCityData?.['name'],
      regionalMultiplier: shape.properties.regionalMultiplier,
      nearestCityData: shape.properties.nearestCityData,
    });
    console.log('[utility-providers]', shape.properties.utilityProviders);
    apiFetch(
      `/api/organizations/${selectedOrg.id}/projects/${projectId}/electric-rates/`,
    )
      .then((data) => console.log('[electric-rates]', data))
      .catch((e) => console.warn('[electric-rates] fetch failed', e));
  }, [selectedShapeId, savedShapes]);

  // Auto-select right panel shape when active subproject changes
  useEffect(() => {
    if (!activeSubprojectId) return;
    const sp = subprojects.find((s) => s.id === activeSubprojectId);
    selectShape(sp?.projectShape ?? null);
  }, [activeSubprojectId, subprojects, selectShape]);

  // Ensure project name is in the store (may be missing on direct navigation)
  useEffect(() => {
    if (!project && projectId && selectedOrg) {
      projectStore.fetch(selectedOrg.id).catch(console.error);
    }
  }, [project, projectId, selectedOrg, projectStore]);

  // Load subprojects on mount
  useEffect(() => {
    if (!projectId || !selectedOrg) return;
    fetchSubprojects(projectId, selectedOrg.id)
      .then((subs) => {
        setSubprojects(subs as Subproject[]);
        // Auto-select if only one subproject
        if (subs.length === 1) {
          setActiveSubprojectId(subs[0].id);
          activeSubprojectIdRef.current = subs[0].id;
        }
      })
      .catch((e) => console.error('Failed to load subprojects:', e));
  }, [projectId, selectedOrg]);

  // ?subprojectId deep-link: auto-select the param subproject on first load.
  // Overrides the subs[0] default. Consumed once; param stays in the URL for back-button.
  const deepLinkAppliedRef = useRef(false);
  useEffect(() => {
    if (deepLinkAppliedRef.current || subprojects.length === 0) return;
    const paramId = searchParams.get('subprojectId');
    if (!paramId) return;
    const match = subprojects.find((s) => s.id === paramId);
    if (!match) return;
    deepLinkAppliedRef.current = true;
    setActiveSubprojectId(match.id);
    activeSubprojectIdRef.current = match.id;
  }, [subprojects, searchParams]);

  // PATCH subproject.project_shape
  const patchSubprojectShape = useCallback(
    async (subprojectId: string, shapeIdValue: string | null) => {
      if (!selectedOrg) return;
      try {
        await apiFetch(
          subprojectEndpoints.BUILDING_DETAIL(selectedOrg.id, subprojectId),
          {
            method: 'PATCH',
            body: JSON.stringify({ projectShape: shapeIdValue }),
          },
        );
        setSubprojects((prev) =>
          prev.map((s) =>
            s.id === subprojectId ? { ...s, projectShape: shapeIdValue } : s,
          ),
        );
      } catch (e) {
        console.error('Failed to patch subproject shape:', e);
      }
    },
    [selectedOrg],
  );

  // Load existing shapes on mount
  const loadShapes = useCallback(async () => {
    if (!projectId) return;
    try {
      const response = await shapeService.list(projectId);
      const features = response.data?.features || [];
      setSavedShapes(features);
    } catch (e) {
      console.error('Failed to load shapes:', e);
    }
  }, [projectId, shapeService]);

  // Refresh derived data (cached utility IDs, regional cost factor, etc.) for all shapes
  const refreshAllShapes = useCallback(async () => {
    if (!projectId || savedShapes.length === 0) return;
    setRefreshing(true);
    try {
      await Promise.all(savedShapes.map((s) => shapeService.refresh(projectId, s.id)));
      await loadShapes();
      setSnackbar('Shape data refreshed');
    } catch {
      setSnackbar('Failed to refresh shape data');
    } finally {
      setRefreshing(false);
    }
  }, [projectId, savedShapes, shapeService, loadShapes]);

  // Save a drawn shape to the backend
  const saveShape = useCallback(
    async (geometry: GeoJSON.Geometry, name: string, color: string) => {
      if (!projectId) return null;
      try {
        const response = await shapeService.create(projectId, {
          type: 'Feature',
          geometry,
          properties: {
            project: projectId,
            name,
            color,
          },
        });
        const shape = response.data;
        if (shape) {
          setSavedShapes((prev) => [...prev, shape]);
          setSelectedShapeId(shape.id);
          selectedShapeIdRef.current = shape.id;
        }
        return shape;
      } catch (e) {
        console.error('Failed to save shape:', e);
        return null;
      }
    },
    [projectId, shapeService],
  );

  // Delete a shape from the backend (called after confirmation)
  const executeDeleteShape = useCallback(
    async (shapeId: string) => {
      if (!projectId) return;
      try {
        await shapeService.delete(projectId, shapeId);
        setSavedShapes((prev) => prev.filter((s) => s.id !== shapeId));
        setSubprojects((prev) =>
          prev.map((s) =>
            s.projectShape === shapeId ? { ...s, projectShape: null } : s,
          ),
        );
        // Remove layer from map
        drawnItemsRef.current.eachLayer((layer: any) => {
          if (layer._shapeId === shapeId) {
            drawnItemsRef.current.removeLayer(layer);
          }
        });
        if (selectedShapeIdRef.current === shapeId) {
          setSelectedShapeId(null);
          selectedShapeIdRef.current = null;
        }
      } catch (e) {
        console.error('Failed to delete shape:', e);
      }
    },
    [projectId, shapeService],
  );

  // Called by SitePanel delete button — always shows confirmation dialog
  const handleDeleteShape = useCallback(
    (shapeId: string) => {
      const shape = savedShapes.find((s) => s.id === shapeId);
      if (!shape) return;
      const assigned = subprojectsRef.current.filter(
        (s) => s.projectShape === shapeId,
      );
      setDeletePending({
        shapeId,
        shapeName: shape.properties.name || 'Unnamed Site',
        subprojectNames: assigned.map((s) => s.name),
      });
    },
    [savedShapes],
  );

  // Called by SitePanel when PATCH saves successfully
  const handleShapeUpdated = useCallback((shape: ProjectShape) => {
    setSavedShapes((prev) => prev.map((s) => (s.id === shape.id ? shape : s)));
  }, []);

  // Trigger assignment flow for a shape (map click or "Select This Site" button)
  const triggerAssign = useCallback(
    (shapeId: string) => {
      const activeId = activeSubprojectIdRef.current;
      if (!activeId) {
        setSnackbar('Select a subproject first');
        return;
      }
      const shape = savedShapes.find((s) => s.id === shapeId);
      if (!shape) return;
      const activeSubproject = subprojectsRef.current.find(
        (s) => s.id === activeId,
      );
      if (!activeSubproject) return;

      // Case C: already has this same site
      if (activeSubproject.projectShape === shapeId) {
        setSnackbar('Already assigned');
        return;
      }

      // Case B: active subproject already has a DIFFERENT site
      if (activeSubproject.projectShape) {
        const currentShape = savedShapes.find(
          (s) => s.id === activeSubproject.projectShape,
        );
        setAssignPending({
          type: 'conflict',
          shapeId,
          shapeName: shape.properties.name || 'Unnamed Site',
          currentSiteName: currentShape?.properties?.name || 'an existing site',
        });
        return;
      }

      // Case A: no site yet
      setAssignPending({
        type: 'confirm',
        shapeId,
        shapeName: shape.properties.name || 'Unnamed Site',
      });
    },
    [savedShapes],
  );

  const handleAssignConfirm = useCallback(async () => {
    if (!assignPending || !activeSubprojectIdRef.current) return;
    setAssignPending(null);
    await patchSubprojectShape(
      activeSubprojectIdRef.current,
      assignPending.shapeId,
    );
  }, [assignPending, patchSubprojectShape]);

  const handleAssignCancel = useCallback(() => {
    setAssignPending(null);
  }, []);

  const handleDeleteConfirm = useCallback(async () => {
    if (!deletePending) return;
    const { shapeId } = deletePending;
    setDeletePending(null);
    await executeDeleteShape(shapeId);
  }, [deletePending, executeDeleteShape]);

  const handleDeleteCancel = useCallback(() => {
    setDeletePending(null);
  }, []);

  const handleRemoveAssignment = useCallback(
    (subprojectId: string, shapeId: string) => {
      const sp = subprojectsRef.current.find((s) => s.id === subprojectId);
      if (!sp) return;
      setRemovePending({ subprojectId, subprojectName: sp.name, shapeId });
    },
    [],
  );

  const handleRemoveConfirm = useCallback(async () => {
    if (!removePending) return;
    setRemovePending(null);
    await patchSubprojectShape(removePending.subprojectId, null);
  }, [removePending, patchSubprojectShape]);

  const handleRemoveCancel = useCallback(() => {
    setRemovePending(null);
  }, []);

  const handleMapDeleteConfirm = useCallback(async () => {
    if (!mapDeletePending) return;
    const { shapeIds } = mapDeletePending;
    setMapDeletePending(null);
    try {
      for (const shapeId of shapeIds) {
        await executeDeleteShape(shapeId);
      }
    } catch {
      setSnackbar('Failed to delete one or more sites. Please try again.');
    }
  }, [mapDeletePending, executeDeleteShape]);

  const handleMapDeleteCancel = useCallback(() => {
    if (!mapDeletePending) return;
    for (const layer of mapDeletePending.layers) {
      drawnItemsRef.current.addLayer(layer);
    }
    selectShape(null);
    setMapDeletePending(null);
  }, [mapDeletePending, selectShape]);

  // Naming dialog — confirm: save shape then fire-and-forget Nominatim + assign subproject
  const handleDialogConfirm = async (name: string) => {
    setDialogOpen(false);
    const layer = pendingLayerRef.current;
    pendingLayerRef.current = null;
    if (!layer || !projectId) return;

    const idx = savedShapesCountRef.current;
    const color = SITE_COLOR_DEFAULTS[idx % SITE_COLOR_DEFAULTS.length];
    const activeId = activeSubprojectIdRef.current;

    const shape = await saveShape(layer.toGeoJSON().geometry, name, color);

    if (shape && activeId) {
      await patchSubprojectShape(activeId, shape.id);
    }

    if (
      shape?.properties.latitude != null &&
      shape.properties.longitude != null
    ) {
      reverseGeocode(shape.properties.latitude, shape.properties.longitude)
        .then((address) => {
          if (!address || !projectId) return;
          return shapeService.update(projectId, shape.id, {
            properties: { address },
          } as any);
        })
        .then((resp: any) => {
          if (resp?.data) {
            setSavedShapes((prev) =>
              prev.map((s) => (s.id === resp.data.id ? resp.data : s)),
            );
          }
        })
        .catch(() => {
          /* geocode failure is non-fatal */
        });
    }
  };

  // Naming dialog — cancel: discard the drawn layer
  const handleDialogCancel = () => {
    setDialogOpen(false);
    if (pendingLayerRef.current) {
      drawnItemsRef.current.removeLayer(pendingLayerRef.current);
      pendingLayerRef.current = null;
    }
  };

  // Initialize map
  useEffect(() => {
    if (!mapContainerRef.current || mapRef.current) return;

    const map = L.map(mapContainerRef.current).setView([39.8283, -98.5795], 4);
    mapRef.current = map;

    // Adds scale bar
    L.control.scale().addTo(map);

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19,
      attribution: '&copy; OpenStreetMap contributors',
    }).addTo(map);

    // ZIP boundaries below drawn shapes so shape clicks take priority
    map.addLayer(zipBoundsRef.current);
    const drawnItems = drawnItemsRef.current;
    map.addLayer(drawnItems);

    const drawControl = new L.Control.Draw({
      position: 'topleft',
      draw: {
        polygon: {
          shapeOptions: {
            color: NEUTRAL_COLOR,
            weight: 3,
            opacity: 0.9,
            fillColor: NEUTRAL_COLOR,
            fillOpacity: 0.2,
          },
        } as any,
        polyline: {
          shapeOptions: { color: NEUTRAL_COLOR, weight: 3, opacity: 0.9 },
        } as any,
        rectangle: false,
        circle: false,
        marker: { icon: new L.Icon.Default() } as any,
        circlemarker: false,
      },
      edit: { featureGroup: drawnItems, remove: true },
    });
    map.addControl(drawControl);

    // Track edit/delete tool modes so shape clicks don't trigger assignment
    map.on('draw:editstart', () => {
      editModeActiveRef.current = true;
    });
    map.on('draw:editstop', () => {
      editModeActiveRef.current = false;
    });
    map.on('draw:deletestart', () => {
      deleteModeActiveRef.current = true;
    });
    map.on('draw:deletestop', () => {
      deleteModeActiveRef.current = false;
    });

    // Check for active subproject before allowing draw
    map.on(L.Draw.Event.DRAWSTART, () => {
      if (!activeSubprojectIdRef.current) {
        setSnackbar('Select a subproject first');
      }
    });

    // Show naming dialog before saving; layer visible as preview while dialog is open
    map.on(L.Draw.Event.CREATED, (event: any) => {
      const layer = event.layer;
      drawnItems.addLayer(layer);
      pendingLayerRef.current = layer;

      const idx = savedShapesCountRef.current;
      const letter = idx < 26 ? String.fromCharCode(65 + idx) : String(idx + 1);
      setDialogDefaultName(`Site ${letter}`);
      setDialogOpen(true);
    });

    // Save edited geometries to the backend when user clicks "Save" on the edit toolbar.
    map.on(L.Draw.Event.EDITED, (event: any) => {
      event.layers.eachLayer((layer: any) => {
        const shapeId = layer._shapeId;
        if (!shapeId || !projectId) return;
        const geometry = layer.toGeoJSON().geometry;
        shapeServiceRef.current
          .update(projectId, shapeId, { geometry })
          .then((resp) => {
            if (resp.data) {
              setSavedShapes((prev) =>
                prev.map((s) => (s.id === shapeId ? resp.data! : s)),
              );
            }
          })
          .catch((e) => console.error('Failed to save shape edit:', e));
      });
    });

    // Handle shape deletion via leaflet-draw toolbar — show confirmation before executing API calls.
    // Leaflet-draw has already removed the layers from drawnItems when this fires (after "Save").
    // On cancel we restore them; on confirm we execute the API deletes.
    map.on(L.Draw.Event.DELETED, (event: any) => {
      const layers: any[] = [];
      const shapeIds: string[] = [];
      const names: string[] = [];
      event.layers.eachLayer((layer: any) => {
        if (layer._shapeId) {
          layers.push(layer);
          shapeIds.push(layer._shapeId);
          names.push(layer._shapeName || 'Unnamed Site');
        }
      });
      if (shapeIds.length > 0) {
        setMapDeletePending({ layers, shapeIds, names });
      }
    });

    map.on('click', () => selectShape(null));

    loadShapes();

    return () => {
      map.remove();
      mapRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Render saved shapes on the map — color from stored shape.properties.color
  useEffect(() => {
    const drawnItems = drawnItemsRef.current;
    if (!mapRef.current) return;

    drawnItems.clearLayers();

    for (const shape of savedShapes) {
      try {
        const color = shape.properties?.color || NEUTRAL_COLOR;
        const geoJsonLayer = L.geoJSON(shape as any, {
          style: {
            color,
            weight: 3,
            opacity: 0.9,
            fillColor: color,
            fillOpacity: 0.15,
          },
        });

        geoJsonLayer.eachLayer((layer: any) => {
          layer._shapeId = shape.id;
          layer._shapeColor = color;
          layer._shapeName = shape.properties?.name || 'Unnamed Site';
          layer.on('click', (e: any) => {
            L.DomEvent.stopPropagation(e);
            // Don't trigger selection or assignment while edit/delete tools are active
            if (editModeActiveRef.current || deleteModeActiveRef.current)
              return;
            const sid = shape.id;
            const currentSelected = selectedShapeIdRef.current;
            if (currentSelected === sid) {
              selectShape(null);
            } else {
              selectShape(sid);
              // Trigger assignment flow if a subproject is active
              if (activeSubprojectIdRef.current) {
                triggerAssign(sid);
              }
            }
          });
          drawnItems.addLayer(layer);
        });
      } catch (e) {
        console.error('Failed to render shape:', shape.id, e);
      }
    }

    if (drawnItems.getLayers().length > 0) {
      mapRef.current.fitBounds(drawnItems.getBounds(), { padding: [50, 50] });
    }

    // Re-apply selection highlight after layers are (re)built.
    //
    // Race condition: two independent async chains run on mount —
    //   A: loadShapes() → setSavedShapes → this effect → Leaflet layers added to drawnItems
    //   B: fetchSubprojects() → deep-link effect → setActiveSubprojectId
    //      → auto-select effect → selectShape() → tries to highlight layers in drawnItems
    //
    // If chain B resolves first, selectShape() correctly sets selectedShapeIdRef.current
    // but finds an empty drawnItems and applies no highlight. This effect then runs later
    // (chain A), rebuilds all layers — and without this block, the shape stays unhighlighted.
    //
    // Fix: after every layer rebuild, check selectedShapeIdRef.current and re-apply.
    const pendingId = selectedShapeIdRef.current;
    if (pendingId) {
      drawnItems.eachLayer((layer: any) => {
        if (layer._shapeId === pendingId) highlightLayer(layer);
      });
    }
  }, [savedShapes, highlightLayer, selectShape, triggerAssign]);

  const zipcodesKey = zipcodes.join(',');

  // Fetch and render ZIP code boundaries
  useEffect(() => {
    if (!projectId || !zipcodesKey) {
      zipBoundsRef.current.clearLayers();
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        const response = await shapeService.getLocationAreas(
          projectId,
          zipcodesKey.split(','),
        );
        if (cancelled) return;
        const features = response.data?.features || [];

        zipBoundsRef.current.clearLayers();
        for (const feature of features) {
          const props = feature.properties as any;
          const code = props?.code || '';
          const name = props?.name || '';

          const layer = L.geoJSON(feature as any, {
            style: {
              color: ZIP_BOUNDARY_COLOR,
              weight: 2,
              opacity: 0.7,
              fillColor: ZIP_BOUNDARY_COLOR,
              fillOpacity: 0.05,
            },
            onEachFeature: (_feat, lyr) => {
              lyr.bindPopup(
                `<strong>${code}</strong>${name ? `<br/>${name}` : ''}`,
                { closeButton: false, offset: [0, -5] },
              );
            },
          });

          layer.eachLayer((l: any) => {
            if (code && l.getBounds) {
              const center = l.getBounds().getCenter();
              const label = L.marker(center, {
                icon: L.divIcon({
                  className: 'zip-label',
                  html: `<span style="font-size:11px;font-weight:600;color:${ZIP_BOUNDARY_COLOR};text-shadow:1px 1px 2px white,-1px -1px 2px white,1px -1px 2px white,-1px 1px 2px white">${code}</span>`,
                  iconSize: [50, 16],
                  iconAnchor: [25, 8],
                }),
                interactive: false,
              });
              zipBoundsRef.current.addLayer(label);
            }
          });
          zipBoundsRef.current.addLayer(layer);
        }
        drawnItemsRef.current.bringToFront();
      } catch (e) {
        console.error('Failed to load ZIP boundaries:', e);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [zipcodesKey, projectId, shapeService]);

  // Toggle ZIP boundary layer visibility
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    if (showZipBounds) {
      if (!map.hasLayer(zipBoundsRef.current)) {
        map.addLayer(zipBoundsRef.current);
        drawnItemsRef.current.bringToFront();
      }
    } else {
      if (map.hasLayer(zipBoundsRef.current)) {
        map.removeLayer(zipBoundsRef.current);
      }
    }
  }, [showZipBounds]);

  const projectName = project?.name || 'Project';

  const handleAddressSelect = useCallback((lat: number, lon: number) => {
    mapRef.current?.flyTo([lat, lon], 16);
  }, []);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        overflow: 'hidden',
      }}
    >
      {/* ── Header ── */}
      <Box
        sx={{
          mt: '35px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          pointerEvents: 'auto',
          ml: '70px',
          flexShrink: 0,
          pb: '15px',
        }}
      >
        <Typography
          variant={'h4'}
          component='h1'
          sx={{
            fontFamily: 'Futura PT, sans-serif',
            fontWeight: 700,
            fontSize: 'clamp(24px, 1.85vw, 35px)',
            color: 'rgba(40, 40, 40, 1)',
            textShadow: {
              xs: '-2px 2px rgba(101, 166, 178, 1)',
              lg: '-4px 3px rgba(101, 166, 178, 1)',
            },
            textTransform: 'uppercase',
            letterSpacing: 'clamp(8px, 0.75vw, 14px)',
            lineHeight: 1,
            mb: 'clamp(4px, 0.4vw, 8px)',
          }}
        >
          PROJECT CREATION
        </Typography>
        <Typography
          variant={'h5'}
          component='h2'
          sx={{
            fontFamily: 'Futura PT, sans-serif',
            fontWeight: 700,
            fontSize: 'clamp(45px, 3.5vw, 68px)',
            color: 'rgba(40, 40, 40, 1)',
            textShadow: {
              xs: '-2px 2px rgba(101, 166, 178, 1)',
              lg: '-4px 3px rgba(101, 166, 178, 1)',
            },
            textTransform: 'uppercase',
            letterSpacing: 'clamp(8px, 0.75vw, 14px)',
            lineHeight: 1,
          }}
        >
          {projectName}
        </Typography>
      </Box>

      {/* ── Main content ── */}
      <Box
        sx={{
          display: 'flex',
          flex: 1,
          overflow: 'hidden',
          paddingLeft: 'clamp(32px, 2.8vw, 54px)',
          paddingRight: 'clamp(44px, 3.8vw, 72px)',
          paddingBottom: 'clamp(24px, 2vw, 40px)',
          gap: 'clamp(16px, 1.5vw, 28px)',
        }}
      >
        {/* ── MAP CARD ── */}
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            bgcolor: 'background.paper',
            borderRadius: 2,
            minWidth: 0,
            p: 2,
            gap: 1.5,
          }}
        >
          {/* Toolbar — 3-column grid for true center alignment */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: '1fr auto 1fr',
              alignItems: 'center',
              flexShrink: 0,
            }}
          >
            {/* Left: Address search — pans map to searched location */}
            <Box>
              <AddressSearch onPlaceSelected={handleAddressSelect} />
            </Box>

            {/* Center: Subproject selector */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography
                variant='body2'
                sx={{
                  color: 'text.secondary',
                  fontSize: '0.8rem',
                  fontWeight: 700,
                  whiteSpace: 'nowrap',
                }}
              >
                Select Site For:
              </Typography>
              {subprojects.length === 0 ? (
                <Typography
                  variant='body2'
                  sx={{
                    color: 'text.secondary',
                    fontSize: '0.8rem',
                    fontStyle: 'italic',
                  }}
                >
                  (no subprojects)
                </Typography>
              ) : (
                <FormControl size='small' sx={{ minWidth: 160 }}>
                  <Select
                    value={activeSubprojectId ?? ''}
                    onChange={(e) => {
                      const val = e.target.value as string;
                      setActiveSubprojectId(val || null);
                      activeSubprojectIdRef.current = val || null;
                    }}
                    displayEmpty
                    sx={{
                      fontSize: '0.8rem',
                      fontWeight: 600,
                      height: 'clamp(22px, 1.8vw, 32px)',
                    }}
                    renderValue={(val) => {
                      if (!val) {
                        return (
                          <Typography
                            sx={{
                              fontSize: '0.8rem',
                              fontStyle: 'italic',
                              color: 'text.secondary',
                            }}
                          >
                            Choose subproject...
                          </Typography>
                        );
                      }
                      const sp = subprojects.find((s) => s.id === val);
                      return sp?.name ?? val;
                    }}
                  >
                    {subprojects.map((sp) => {
                      const assignedShape = savedShapes.find(
                        (s) => s.id === sp.projectShape,
                      );
                      const dotColor =
                        assignedShape?.properties?.color || NEUTRAL_COLOR;
                      return (
                        <MenuItem key={sp.id} value={sp.id}>
                          <Box
                            sx={{
                              width: 10,
                              height: 10,
                              borderRadius: '50%',
                              bgcolor: dotColor,
                              mr: 1,
                              flexShrink: 0,
                            }}
                          />
                          <Typography sx={{ fontSize: '0.85rem', flex: 1 }}>
                            {sp.name}
                          </Typography>
                          {sp.projectShape && (
                            <CheckIcon
                              sx={{
                                fontSize: '0.9rem',
                                color: 'success.main',
                                ml: 0.5,
                              }}
                            />
                          )}
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>
              )}
            </Box>

            {/* Right: Refresh Data + Zip Boundaries toggle + Save & Back */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-end',
                gap: 1,
              }}
            >
              {savedShapes.length > 0 && (
                <Button
                  size="small"
                  variant="outlined"
                  disabled={refreshing}
                  onClick={refreshAllShapes}
                  sx={{
                    fontSize: 'clamp(9px, 0.7vw, 12px)',
                    fontFamily: 'Inter, sans-serif',
                    fontWeight: 600,
                    px: 1,
                    py: 0.25,
                    minWidth: 0,
                    height: 'clamp(20px, 1.6vw, 30px)',
                    whiteSpace: 'nowrap',
                    textTransform: 'none',
                    borderColor: 'text.secondary',
                    color: 'text.secondary',
                  }}
                >
                  {refreshing ? 'Refreshing…' : 'Refresh Data'}
                </Button>
              )}
              <Button
                size='small'
                variant={showZipBounds ? 'contained' : 'outlined'}
                onClick={() => setShowZipBounds((v) => !v)}
                sx={{
                  fontSize: 'clamp(9px, 0.7vw, 12px)',
                  fontFamily: 'Inter, sans-serif',
                  fontWeight: 600,
                  px: 1,
                  py: 0.25,
                  minWidth: 0,
                  height: 'clamp(20px, 1.6vw, 30px)',
                  whiteSpace: 'nowrap',
                  textTransform: 'none',
                  borderColor: 'info.main',
                  color: showZipBounds ? 'background.paper' : 'info.main',
                  bgcolor: showZipBounds ? 'info.main' : 'transparent',
                  '&:hover': {
                    bgcolor: showZipBounds
                      ? 'info.dark'
                      : 'rgba(123,188,179,0.12)',
                    borderColor: 'info.main',
                  },
                }}
              >
                {showZipBounds ? 'Hide' : 'Show'} Zip Boundaries
              </Button>
              <IconButton
                size='small'
                onClick={handleBack}
                sx={{ color: 'text.secondary' }}
                aria-label='Save and back'
              >
                <ReplyIcon fontSize='small' />
              </IconButton>
            </Box>
          </Box>

          {/* Leaflet canvas */}
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              borderRadius: 1.5,
              overflow: 'hidden',
            }}
          >
            <Box ref={mapContainerRef} sx={{ width: '100%', height: '100%' }} />
          </Box>
        </Box>

        {/* ── SITES PANEL ── */}
        <SitePanel
          shapes={savedShapes}
          selectedShapeId={selectedShapeId}
          projectName={projectName}
          projectId={projectId!}
          subprojects={subprojects}
          onSelectShape={selectShape}
          onShapeUpdated={handleShapeUpdated}
          onDeleteShape={handleDeleteShape}
          onSelectThisSite={triggerAssign}
          onRemoveSubprojectAssignment={handleRemoveAssignment}
        />
      </Box>

      {/* ── Footer ── */}
      <Box sx={{ flexShrink: 0, width: '100%' }}>
        <BaseDecarbFooterBar
          backText={'Cancel'}
          onBackClick={handleBack}
          onNextClick={() =>
            navigate(`../${Routes.Project.CostEstimates.path}`)
          }
          nextIsDisabled={isIncomplete}
          nextIsDisabledText={'INCOMPLETE DATA'}
          onReturnToHubClick={handleSummary}
          returnText={
            pencilsDown ? 'RETURN TO SUMMARY' : 'SAVE & EXIT TO SUMMARY'
          }
        />
      </Box>

      {/* ── New Site naming dialog ── */}
      <NewSiteDialog
        open={dialogOpen}
        defaultName={dialogDefaultName}
        onConfirm={handleDialogConfirm}
        onCancel={handleDialogCancel}
      />

      {/* ── Assignment dialogs ── */}
      {assignPending?.type === 'confirm' && (
        <ConfirmAssignDialog
          open
          siteName={assignPending.shapeName}
          subprojectName={
            subprojects.find((s) => s.id === activeSubprojectId)?.name ?? ''
          }
          onConfirm={handleAssignConfirm}
          onCancel={handleAssignCancel}
        />
      )}
      {assignPending?.type === 'conflict' && (
        <ConflictAssignDialog
          open
          subprojectName={
            subprojects.find((s) => s.id === activeSubprojectId)?.name ?? ''
          }
          currentSiteName={assignPending.currentSiteName ?? ''}
          newSiteName={assignPending.shapeName}
          onConfirm={handleAssignConfirm}
          onCancel={handleAssignCancel}
        />
      )}
      {deletePending && (
        <DeleteSiteDialog
          open
          siteName={deletePending.shapeName}
          subprojectNames={deletePending.subprojectNames}
          onConfirm={handleDeleteConfirm}
          onCancel={handleDeleteCancel}
        />
      )}
      {removePending && (
        <RemoveAssignmentDialog
          open
          subprojectName={removePending.subprojectName}
          onConfirm={handleRemoveConfirm}
          onCancel={handleRemoveCancel}
        />
      )}
      {mapDeletePending && (
        <MapDeleteConfirmDialog
          open
          names={mapDeletePending.names}
          onConfirm={handleMapDeleteConfirm}
          onCancel={handleMapDeleteCancel}
        />
      )}

      {/* ── Snackbar ── */}
      <Snackbar
        open={!!snackbar}
        message={snackbar}
        autoHideDuration={2500}
        onClose={() => setSnackbar(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
}
